/**
 * Editar Pagamento — os mesmos campos do Registrar Pagamento, sem o comprovante.
 *
 * Só o que mudou vai no PUT: o servidor compara campo a campo e registra o que foi alterado, e
 * reenviar um valor antigo que o schema de edição já não aceita (meio BOLETO, conta desativada)
 * recusaria a edição inteira por um campo que ninguém tocou. Quais campos podem mudar vem do
 * servidor (`editableFields`): com parte efetivada só o valor; boleto, só data e observação.
 */
import { useEffect, useMemo, useState } from 'react';
import { FaTimes } from 'react-icons/fa';
import { toast } from 'react-toastify';

import PaymentDateInput, { toIsoInstant } from '../../../molecules/payment/PaymentDateInput';
import PaymentTypeFields, {
    PaymentTypeValue, emptyPaymentTypeValue, assetDationTotal,
} from '../../../molecules/payment/PaymentTypeFields';
import PayerFields, { PayerValue, emptyPayer, onlyDigits } from '../../../molecules/payment/PayerFields';
import { emptyAsset } from '../../../molecules/payment/AssetDationForm';
import { buildPayerBlock } from '../../../molecules/payment/paymentPayload';
import { AssetType, PaymentMethod, PaymentType } from '../../../../constants/payment';
import CurrencyInput from '../../../molecules/CurrencyInput';
import TextEdit from '../../../molecules/TextEdit';
import {
    PaymentDetail, PaymentDetailPayer, PaymentEditableField, UpdatePaymentPayload, getPayment, updatePayment,
} from '../services/billingApi';
import { formatBRL } from '../../../../utils/currencyUtils';
import NotesList from '../../../molecules/NotesList';

const label = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
const input = 'w-full px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg dark:bg-slate-700 dark:text-white';

/** O edit-payment.schema.json não aceita BOLETO: boleto pago chega pelo BOLETO_PAID. */
const EDITABLE_METHODS: string[] = [PaymentMethod.PIX, PaymentMethod.TRANSFER];

const pad = (n: number) => String(n).padStart(2, '0');

/** Inverso do `toIsoInstant`: o instante UTC nas partes locais que o PaymentDateInput edita. */
export const localPartsOf = (iso: string | null): { date: string; time: string } => {
    const d = iso ? new Date(iso) : null;
    if (!d || Number.isNaN(d.getTime())) return { date: '', time: '00:00' };
    return {
        date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
        time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
    };
};

const minuteOf = (iso: string | null | undefined): number | null => {
    if (!iso) return null;
    const t = new Date(iso).getTime();
    return Number.isNaN(t) ? null : Math.floor(t / 60000);
};

const cents = (value: number | null | undefined) => Math.round(Number(value || 0) * 100);

export const typeFieldsOf = (detail: PaymentDetail): PaymentTypeValue => {
    const account = detail.receivingBankAccount;
    return emptyPaymentTypeValue({
        payment_type: detail.paymentType,
        payment_method: detail.paymentType === PaymentType.FINANCIAL && detail.paymentMethod
            ? detail.paymentMethod : PaymentMethod.PIX,
        // o select de contas trabalha com o alias; o uuid só entra se o alias faltar
        receiving_bank_account: account?.aliasName || account?.receivingBankAccountId || '',
        debt_confession_contract: detail.debtConfessionContract || '',
        asset_dation: detail.paymentType === PaymentType.ASSET_DATION
            ? detail.assetDation
                ?? { ...emptyAsset((detail.paymentMethod as AssetType) || undefined), estimated_value: detail.amount }
            : null,
    });
};

export const payerOf = (payer: PaymentDetailPayer | null): PayerValue => emptyPayer(payer ? {
    document: payer.documentNumber || '',
    name: payer.name || '',
    personId: payer.externalId || null,
    email: payer.email || '',
    phone: payer.phone || '',
    address_street: payer.addressStreet || '',
    address_number: payer.addressNumber || '',
    address_complement: payer.addressComplement || '',
    address_neighborhood: payer.addressNeighborhood || '',
    address_city: payer.addressCity || '',
    address_state: payer.addressState || '',
    address_zip_code: payer.addressZipCode || '',
    lookup: payer.externalId ? 'found' : 'idle',
} : {});

/** Tipo, meio, conta, bem e contrato andam juntos: o schema exige a conta sempre que o corpo declara
 *  FINANCIAL, e o servidor apaga o bem ou o contrato do tipo anterior quando o tipo muda. */
const typeBlockOf = (t: PaymentTypeValue) => {
    const financial = t.payment_type === PaymentType.FINANCIAL;
    const dation = t.payment_type === PaymentType.ASSET_DATION;
    const confession = t.payment_type === PaymentType.DEBT_CONFESSION;
    return {
        payment_type: t.payment_type,
        payment_method: financial ? t.payment_method
            : dation ? t.asset_dation?.asset_type ?? null : null,
        receiving_bank_account_id: financial ? t.receiving_bank_account : null,
        ...(dation ? { asset_dation: t.asset_dation } : {}),
        ...(confession ? { debt_confession_contract: t.debt_confession_contract.trim() || null } : {}),
    };
};

const same = (a: unknown, b: unknown) => JSON.stringify(a) === JSON.stringify(b);

interface FormProps {
    detail: PaymentDetail;
    onClose: () => void;
    onSaved: () => void;
}

const EditPaymentForm: React.FC<FormProps> = ({ detail, onClose, onSaved }) => {
    const initial = useMemo(() => {
        const { date, time } = localPartsOf(detail.paymentDate);
        return {
            date, time,
            typeFields: typeFieldsOf(detail),
            payer: payerOf(detail.payer),
            notes: '',
        };
    }, [detail]);

    const [amount, setAmount] = useState(Number(detail.amount || 0));
    const [date, setDate] = useState(initial.date);
    const [time, setTime] = useState(initial.time);
    const [showTime, setShowTime] = useState(initial.time !== '00:00');
    const [typeFields, setTypeFields] = useState<PaymentTypeValue>(initial.typeFields);
    const [payer, setPayer] = useState<PayerValue>(initial.payer);
    const [notes, setNotes] = useState(initial.notes);
    const [saving, setSaving] = useState(false);

    const can = (field: PaymentEditableField) => detail.editableFields.includes(field);
    const amountLocked = !can('amount');
    const dateLocked = !can('payment_date');
    const typeLocked = !can('payment_type');
    const payerLocked = !can('payer_id');
    const notesLocked = !can('notes');
    const frozen = detail.editableFields.length === 0;
    const boleto = detail.paymentMethod === PaymentMethod.BOLETO;
    const amountOnly = !amountLocked && detail.editableFields.length === 1;
    const minAmount = Number(detail.minAmount || 0);
    const belowMin = minAmount > 0 && cents(amount) < cents(minAmount);

    const methods = useMemo(() => (
        detail.paymentMethod && detail.paymentType === PaymentType.FINANCIAL
            && !EDITABLE_METHODS.includes(detail.paymentMethod)
            ? [...EDITABLE_METHODS, detail.paymentMethod] : EDITABLE_METHODS
    ), [detail]);

    const payload = useMemo((): UpdatePaymentPayload => {
        const changes: UpdatePaymentPayload = {};
        if (!amountLocked && cents(amount) !== cents(detail.amount)) changes.amount = amount;

        const instant = toIsoInstant(date, time, showTime);
        if (!dateLocked && instant && minuteOf(instant) !== minuteOf(detail.paymentDate)) {
            changes.payment_date = instant;
        }

        const typeBlock = typeBlockOf(typeFields);
        if (!typeLocked && !same(typeBlock, typeBlockOf(initial.typeFields))) Object.assign(changes, typeBlock);

        const payerBlock = buildPayerBlock(payer);
        if (!payerLocked && !same(payerBlock, buildPayerBlock(initial.payer))) {
            changes.payer = payerBlock as UpdatePaymentPayload['payer'];
        }

        if (!notesLocked && notes.trim()) changes.notes = notes.trim();
        return changes;
    }, [amount, amountLocked, dateLocked, typeLocked, payerLocked, notesLocked,
        date, time, showTime, typeFields, payer, notes, detail, initial]);

    const hasChanges = Object.keys(payload).length > 0;
    const fieldsValid = (dateLocked || !!date)
        && (payerLocked || onlyDigits(payer.document).length >= 11)
        && (typeLocked || typeFields.payment_type !== PaymentType.FINANCIAL || !!typeFields.receiving_bank_account);
    const canSave = !frozen && amount > 0 && !belowMin && fieldsValid && hasChanges && !saving;

    const handleAmountChange = (next: number) => {
        setAmount(next);
        if (typeFields.payment_type !== PaymentType.ASSET_DATION) return;
        const asset = typeFields.asset_dation ?? emptyAsset();
        if (asset.estimated_value === next) return;
        setTypeFields({ ...typeFields, asset_dation: { ...asset, estimated_value: next } });
    };

    const save = async () => {
        setSaving(true);
        try {
            await updatePayment(detail.paymentId, payload);
            toast.success('Pagamento atualizado');
            onSaved();
            onClose();
        } catch (e: any) {
            toast.error(e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <>
            <div className="px-6 py-4 overflow-y-auto">
                {(frozen || amountOnly || boleto) && (
                    <p className="mb-4 px-3 py-2 text-sm rounded bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
                        {frozen
                            ? 'Pagamento de boleto com parte já efetivada: não pode mais ser alterado.'
                            : amountOnly
                                ? 'Parte deste pagamento já foi efetivada: só o valor pode ser alterado.'
                                : 'Pagamento de boleto: valor, meio e pagador vêm do banco; só a data e a observação podem ser alterados.'}
                    </p>
                )}
                <div className="grid grid-cols-2 gap-4">
                    <fieldset disabled={dateLocked}>
                        <PaymentDateInput label="Data do Pagamento *" date={date} time={time} showTime={showTime}
                            disabled={dateLocked} id="editar-pagamento-data"
                            onChange={next => { setDate(next.date); setTime(next.time); setShowTime(next.showTime); }} />
                    </fieldset>
                    <fieldset disabled={amountLocked}>
                        <CurrencyInput
                            id="editar-pagamento-valor"
                            label="Valor *"
                            prefix="R$"
                            fractionDigits={2}
                            value={amount}
                            onChange={handleAmountChange}
                            className="mb-0"
                        />
                        {minAmount > 0 && (
                            <p className={`text-xs mt-1 ${belowMin ? 'text-red-600' : 'text-gray-500'}`}>
                                {belowMin
                                    ? `O valor não pode ficar abaixo de ${formatBRL(minAmount)} (efetivado + sinalizado).`
                                    : `Mínimo: ${formatBRL(minAmount)} (efetivado + sinalizado)`}
                            </p>
                        )}
                    </fieldset>
                </div>

                <fieldset disabled={typeLocked}>
                    <div className="mt-4">
                        <PaymentTypeFields
                            value={typeFields}
                            onChange={next => {
                                const total = assetDationTotal(next.asset_dation);
                                if (next.payment_type === PaymentType.ASSET_DATION
                                    && total !== assetDationTotal(typeFields.asset_dation)) {
                                    setAmount(total);
                                }
                                setTypeFields(next);
                            }}
                            disabled={typeLocked}
                            methods={methods}
                            labelClassName={label}
                            inputClassName={input}
                            idPrefix="editar-pagamento"
                            chargeAttributes={false}
                            currentAccount={detail.receivingBankAccount}
                        />
                    </div>

                </fieldset>

                <fieldset disabled={payerLocked} className="mt-6 border-t dark:border-slate-700 pt-4">
                    <PayerFields value={payer} onChange={setPayer} idPrefix="editar-pagamento-pagador"
                        labelClassName={label} inputClassName={input} />
                </fieldset>

                <fieldset disabled={notesLocked} className="mt-4">
                    {(detail.notesList || []).length > 0 && (
                        <div className="mb-2">
                            <p className={label}>Observações anteriores</p>
                            <NotesList notes={detail.notesList || []} label="Observações anteriores" />
                        </div>
                    )}
                    <TextEdit
                        id="editar-pagamento-obs"
                        label="Nova observação (somada às anteriores)"
                        value={notes}
                        disabled={notesLocked}
                        onChange={e => setNotes(e.target.value)}
                        placeholder=" "
                    />
                </fieldset>
            </div>

            <div className="px-6 py-4 border-t dark:border-slate-600 flex justify-end gap-2">
                <button onClick={onClose} className="px-4 py-2 text-sm text-gray-600 dark:text-gray-300">
                    Cancelar
                </button>
                <button onClick={save} disabled={!canSave}
                    title={!hasChanges ? 'Nenhuma alteração a salvar' : undefined}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg disabled:opacity-50 hover:bg-blue-700">
                    {saving ? 'Salvando...' : 'Salvar'}
                </button>
            </div>
        </>
    );
};

interface Props {
    paymentId: string;
    onClose: () => void;
    /** chamado após salvar, para a tela recarregar o dia */
    onSaved: () => void;
}

const EditPaymentModal: React.FC<Props> = ({ paymentId, onClose, onSaved }) => {
    const [detail, setDetail] = useState<PaymentDetail | null>(null);
    const [failed, setFailed] = useState(false);

    useEffect(() => {
        let active = true;
        getPayment(paymentId)
            .then(found => { if (active) setDetail(found); })
            .catch((e: any) => {
                if (!active) return;
                setFailed(true);
                toast.error(`Não foi possível carregar o pagamento: ${e.message}`);
            });
        return () => { active = false; };
    }, [paymentId]);

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div role="dialog" aria-label="Editar Pagamento"
                className="bg-white dark:bg-slate-800 rounded-lg shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col">
                <div className="flex items-center justify-between px-6 py-4 border-b dark:border-slate-600">
                    <div>
                        <h3 className="text-lg font-semibold">Editar Pagamento</h3>
                        <p className="text-xs text-gray-500 mt-0.5">#{paymentId.slice(0, 8)}</p>
                    </div>
                    <button onClick={onClose} aria-label="Fechar"
                        className="text-gray-400 hover:text-gray-600"><FaTimes /></button>
                </div>

                {detail ? (
                    <EditPaymentForm detail={detail} onClose={onClose} onSaved={onSaved} />
                ) : (
                    <p className="px-6 py-8 text-center text-gray-500">
                        {failed ? 'Não foi possível carregar o pagamento.' : 'Carregando...'}
                    </p>
                )}
            </div>
        </div>
    );
};

export default EditPaymentModal;
